// What the 16 pads play and how input reaches the engine: computer keyboard (same mapping as the
// desktop app: Z X C V / A S D F / Q W E R / 1 2 3 4, pad 1 bottom-left), mouse/touch velocity,
// note repeat, and Web MIDI. Instrument targets play the scale's notes only (key lock).
import {
  FIRST_ATMOS,
  GRID_TICKS,
  keyLockNote,
  NUM_ATMOS_PADS,
  ROLE_RANGE,
  scaleNotesInRange,
  SLOT_ROLE,
} from "@eb/shared";
import { editorAudio } from "./audio";
import { songKeyOf, useEditor } from "./store";

/** The note each pad plays for an instrument target: 16 ascending scale notes from the range's
 *  lowest tonic (shifted by octaves). For drums, −1 (the pad's own sound). */
export function padNotes(target: number, octave: number): number[] {
  const s = useEditor.getState();
  if (target < 16 || target === FIRST_ATMOS || !s.def) return Array.from({ length: 16 }, () => -1);
  const range = ROLE_RANGE[SLOT_ROLE[target]];
  const key = songKeyOf(s)!;
  const notes = scaleNotesInRange(key, range.lo, range.hi + 24);
  const firstTonic = notes.findIndex((n) => (((n - key.tonic) % 12) + 12) % 12 === 0);
  const start = Math.max(0, firstTonic + octave * 7);
  return Array.from({ length: 16 }, (_, i) => keyLockNote(notes[Math.min(notes.length - 1, start + i)], target, key));
}

/** The slot and note a pad press plays (slot −1: nothing — the atmos kit has 12 pads). */
export function padTarget(pad: number): { slot: number; note: number } {
  const s = useEditor.getState();
  if (s.padTarget < 16) return { slot: pad, note: -1 };
  if (s.padTarget === FIRST_ATMOS) return { slot: pad < NUM_ATMOS_PADS ? FIRST_ATMOS + pad : -1, note: -1 };
  return { slot: s.padTarget, note: padNotes(s.padTarget, s.padOctave)[pad] };
}

const held = new Map<string, { slot: number; note: number }>();
export const padListeners = new Set<(pad: number, on: boolean, velocity: number) => void>();

export async function padDown(pad: number, velocity: number, source = "ui"): Promise<void> {
  const k = `${source}:${pad}`;
  if (held.has(k)) return;
  const t = padTarget(pad);
  if (t.slot < 0) return;
  held.set(k, t);
  for (const l of padListeners) l(pad, true, velocity);
  const h = await editorAudio();
  const s = useEditor.getState();
  if (s.noteRepeat && t.slot < 16) h.noteRepeat(true, GRID_TICKS[s.repeatGrid]);
  h.noteOn(t.slot, velocity, t.note);
}

export async function padUp(pad: number, source = "ui"): Promise<void> {
  const k = `${source}:${pad}`;
  const t = held.get(k);
  if (!t) return;
  held.delete(k);
  for (const l of padListeners) l(pad, false, 0);
  const h = await editorAudio();
  h.noteOff(t.slot, t.note);
  if (![...held.values()].some((x) => x.slot < 16)) h.noteRepeat(false, 240);
}

/** Audition a sound (clicking a grid label or placing a note). */
export function previewSound(slot: number, note: number, velocity: number): void {
  void editorAudio().then((h) => {
    h.noteOn(slot, velocity, note);
    setTimeout(() => h.noteOff(slot, note), 260);
  });
}

// ---------------------------------------------------------------- Web MIDI

let midiAccess: MIDIAccess | null = null;

/** Enable Web MIDI input (where available). Notes 36–51 → pads; with an instrument target any
 *  note plays that instrument, snapped to the key. */
export async function enableMidi(): Promise<{ ok: boolean; inputs: string[]; reason?: string }> {
  if (!("requestMIDIAccess" in navigator)) return { ok: false, inputs: [], reason: "This browser has no Web MIDI." };
  try {
    midiAccess ??= await navigator.requestMIDIAccess();
  } catch (e) {
    return { ok: false, inputs: [], reason: String(e) };
  }
  const attach = () => {
    for (const input of midiAccess!.inputs.values()) input.onmidimessage = onMidi;
  };
  attach();
  midiAccess.onstatechange = attach;
  return { ok: true, inputs: [...midiAccess.inputs.values()].map((i) => i.name ?? "MIDI input") };
}

function onMidi(e: MIDIMessageEvent) {
  const d = e.data;
  if (!d || d.length < 3) return;
  const cmd = d[0] & 0xf0;
  const note = d[1];
  const vel = d[2];
  const s = useEditor.getState();
  if (s.padTarget >= 16 && s.padTarget !== FIRST_ATMOS && s.def) {
    const n = keyLockNote(note, s.padTarget, songKeyOf(s)!);
    void editorAudio().then((h) => {
      if (cmd === 0x90 && vel > 0) h.noteOn(s.padTarget, vel, n);
      else if (cmd === 0x80 || (cmd === 0x90 && vel === 0)) h.noteOff(s.padTarget, n);
    });
    return;
  }
  const pad = note - 36;
  if (pad < 0 || pad > 15) return;
  if (cmd === 0x90 && vel > 0) void padDown(pad, vel, "midi");
  else if (cmd === 0x80 || (cmd === 0x90 && vel === 0)) void padUp(pad, "midi");
}
